/** Estimates server "now" so deck.gl currentTime matches feed timestamps. */
export class ServerClock {
  private offset = 0;
  private syncedAt: number | null = null;
  constructor(private localNow: () => number = () => Date.now()) {}
  /**
   * @param serverTimeMs The server's "now" estimate. For a message that may
   *   have sat in an edge cache (e.g. a CF-cached /snapshot HIT), callers
   *   should pre-add the response's Age (in ms) so this stays a true "now"
   *   rather than the cached response's original generation time.
   * @param staleForMs How stale the underlying DATA already was when this
   *   sync happened (e.g. a cached snapshot's Age, in ms) — defaults to 0 for
   *   messages that are always fresh (WS deltas, uncached responses).
   *   Backdates `syncedAt` so `msSinceLastSync()` reflects true data
   *   staleness, not just "time since this sync() call" — otherwise a 30s
   *   stale cached snapshot would read as freshly-synced immediately after
   *   fetch, wrongly letting a (re)connect skip the WS's full snapshot.
   */
  sync(serverTimeMs: number, staleForMs = 0): void {
    const now = this.localNow();
    this.offset = serverTimeMs - now;
    this.syncedAt = now - staleForMs;
  }
  /**
   * Milliseconds since the most recent sync, or null if never synced. Every
   * applied server message syncs the clock, so this doubles as "how fresh is
   * the client's layer state" — used to decide whether a (re)connect can skip
   * the server's multi-MB on-connect snapshot.
   */
  msSinceLastSync(): number | null {
    return this.syncedAt === null ? null : this.localNow() - this.syncedAt;
  }
  nowMs(): number {
    return this.syncedAt !== null ? this.localNow() + this.offset : this.localNow();
  }
  nowSec(): number {
    return this.nowMs() / 1000;
  }
}
